import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { or } from 'ember-truth-helpers';
import CharacterCounter from '../core/character-counter.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import FlexRow from '../layout/flex/row.gjs';
import HelperText from '../core/helper-text.gjs';
import LabeledBase from './labeled/base.gjs';
import Super from '../core/base.gjs';
import styles from './text.module.scss';

const FLEX_DEFAULT = '1';

const METADATA = Metadata.define({
  description: `Text fields used to gather information and input. The input will fill the available width of its container.

### Named blocks
This component supports two named blocks to support more complex content for labels and helper text:

* \`:label\`
* \`:helper-text\``,
  extends: [
    pick(ACTION_ARGS, [
      'onBlur',
      'onChange',
      'onClick',
      'onFocus',
      'onInput',
      'onKeyDown',
      'onKeyUp',
    ]),
    pick(COMPONENT_STATE_ARGS, [
      'disabled',
      'error',
      { name: 'id', initialValue: 'styleguide-text-input' },
    ]),
    pick(INPUT_FORM_ARGS, [
      { name: 'helperText', initialValue: 'Helper text' },
      { name: 'label', initialValue: 'Label' },
      'name',
      { name: 'placeholder', initialValue: 'Placeholder' },
      'required',
      { name: 'value', initialValue: '' },
    ]),
  ],
  meta: {
    charLimit: Arg.number({ description: 'Maximum character count; enables character counter' }),
  },
  name: 'Text',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=23618-561707&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/471adb-text-input',
  },
});

export default class SpdsInputText extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-text';
  styles = styles;

  get characterCounterId() {
    return this.composeSubcomponentId('character-counter');
  }

  get flex() {
    return this.args.flex ?? FLEX_DEFAULT;
  }

  get helperTextId() {
    return this.composeSubcomponentId('helper-text');
  }

  get showAdditionalText() {
    let { charLimit, helperText } = this.args;

    return charLimit || helperText;
  }

  <template>
    <FlexColumn @flex={{this.flex}} @gap="4px">
      <LabeledBase
        @additionalArguments={{this.arguments}}
        @ariaLabel={{@ariaLabel}}
        @autofocus={{@autofocus}}
        @component={{this.constructor.metadata.component}}
        @disabled={{@disabled}}
        @error={{@error}}
        @id={{this.id}}
        @maxLength={{@charLimit}}
        @name={{@name}}
        @onBlur={{@onBlur}}
        @onChange={{@onChange}}
        @onClick={{@onClick}}
        @onFocus={{@onFocus}}
        @onInput={{@onInput}}
        @onKeyDown={{@onKeyDown}}
        @onKeyUp={{@onKeyUp}}
        @placeholder={{@placeholder}}
        @required={{@required}}
        @type="text"
        @value={{@value}}
        class={{this.classes}}
        data-test-spds-text-input={{this.id}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if (or @label (has-block "label"))}}
          <FlexRow @content={{@label}} class="spds-text-input-label-container">
            {{yield to="label"}}
          </FlexRow>
        {{/if}}
      </LabeledBase>
      {{#if (or this.showAdditionalText (has-block "helper-text"))}}
        <FlexRow class="spds-text-input-additional-text-container">
          <HelperText
            @error={{@error}}
            @helperText={{@helperText}}
            @id={{this.helperTextId}}
            class="spds-text-input-helper-text"
            data-test-spds-text-input-helper-text={{this.id}}
          >
            {{yield to="helper-text"}}
          </HelperText>
          {{#if @charLimit}}
            <CharacterCounter
              @id={{this.characterCounterId}}
              @limit={{@charLimit}}
              @value={{@value}}
              data-test-spds-text-input-char-count={{this.id}}
            />
          {{/if}}
        </FlexRow>
      {{/if}}
    </FlexColumn>
  </template>
}

export const INPUT_TEXT_METADATA = SpdsInputText.metadata;
